import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Icons } from "../lib/Icons";
import { dedupeProvisionLines } from "../lib/dedupeProvisionLines";

// Keyword → area-of-need classifier mirroring functions-new/src/v2/sectionFClassifier.ts
// Used to pre-fill the four area textareas when the EHCP extraction stored a flat
// `provision` list (or `v2Categories`) without an explicit `provisionByArea` split.
const AREA_KEYWORDS = {
  communicationAndInteraction: [
    "salt", "speech and language", "speech & language", "speech-language",
    "language therap", "communication", "social communication", "social skills",
    "lego therapy", "attention autism", "pecs", "aac", "colourful semantics",
    "talkabout", "social stories", "narrative therap", "blank level", "makaton",
  ],
  cognitionAndLearning: [
    "phonics", "read", "literacy", "writing", "spelling", "handwriting",
    "math", "numeracy", "precision teaching", "dyslexia", "comprehension",
    "working memory", "metacognition", "toe by toe", "fresh start", "rapid",
    "lexia", "nessy", "ipeels", "scaffold", "pre-teach", "preteach",
    "differentiat", "modified curriculum", "small steps", "ta support",
    "hlta", "lsa", "1:1 support", "1-to-1 support", "key worker",
  ],
  socialEmotionalMentalHealth: [
    "elsa", "emotional literacy", "emotional regulation", "regulation",
    "zones of regulation", "nurture", "mental health", "counselling",
    "anxiety", "self-esteem", "behaviour", "behavior", "trauma",
    "mindfulness", "drawing and talking", "draw and talk", "pastoral",
    "thrive", "mentoring", "play therap",
  ],
  sensoryAndPhysical: [
    "sensory", "ot ", "occupational therap", "physio", "fine motor",
    "gross motor", "motor skills", "handwriting tools", "pencil grip",
    "fidget", "wobble cushion", "ear defender", "movement break",
    "sensory circuit", "sensory diet", "low arousal", "visual support",
    "visual timetable", "now and next", "weighted", "chewy", "proprioceptive",
    "vestibular", "ramp", "accessible", "hearing", "vision impair",
  ],
};

const SOURCE_CATEGORY_FALLBACK = {
  EducationalProvision: "cognitionAndLearning",
  StaffingAndFrequency: "cognitionAndLearning",
  SpecialistInput: "communicationAndInteraction",
  EnvironmentAndResources: "sensoryAndPhysical",
};

function classifyLineToArea(line, sourceCategory) {
  const hay = String(line || "").toLowerCase();
  if (!hay.trim()) return null;
  let best = null;
  let bestScore = 0;
  for (const [area, kws] of Object.entries(AREA_KEYWORDS)) {
    for (const kw of kws) {
      if (hay.includes(kw) && kw.length > bestScore) {
        best = area;
        bestScore = kw.length;
      }
    }
  }
  if (best) return best;
  if (sourceCategory && SOURCE_CATEGORY_FALLBACK[sourceCategory]) {
    return SOURCE_CATEGORY_FALLBACK[sourceCategory];
  }
  return "cognitionAndLearning";
}

function autoClassifyProvisions({ flatList, v2Categories }) {
  const buckets = {
    communicationAndInteraction: [],
    cognitionAndLearning: [],
    socialEmotionalMentalHealth: [],
    sensoryAndPhysical: [],
  };
  const seen = new Set();
  const push = (line, area) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) return;
    const key = `${area}::${trimmed.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    buckets[area].push(trimmed);
  };
  if (v2Categories && typeof v2Categories === "object") {
    for (const cat of Object.keys(v2Categories)) {
      const raw = v2Categories[cat];
      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
      for (const line of list) {
        if (typeof line !== "string" || !line.trim() || line === "No provision provided") continue;
        const area = classifyLineToArea(line, cat);
        push(line, area);
      }
    }
  }
  if (Array.isArray(flatList) && flatList.length) {
    for (const line of flatList) {
      if (typeof line !== "string" || !line.trim()) continue;
      const area = classifyLineToArea(line, null);
      push(line, area);
    }
  }
  return buckets;
}

export default function EHCPSectionF({ studentId, onSaved }) {
  const [textCni, setTextCni] = useState("");
  const [textCal, setTextCal] = useState("");
  const [textSemh, setTextSemh] = useState("");
  const [textSp, setTextSp] = useState("");
  const [provisions, setProvisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  const parseLines = (text) => {
    return (text || "")
      .split(/\r?\n/)
      .map((s) => s.replace(/^([•\-\*–]|\d+[\.\)])\s+/, "").trim())
      .filter(Boolean);
  };

  const load = async () => {
    try {
      setLoading(true);
      const ref = doc(db, `students/${studentId}`);
      const snap = await getDoc(ref);
      const data = snap.exists() ? snap.data() : {};
      const sectionF = data?.ehcpDetails?.sectionF || {};
      const byArea = sectionF.provisionByArea || null;
      const list = Array.isArray(sectionF.provision) ? sectionF.provision : [];
      const v2 = sectionF.v2Categories || null;

      if (byArea && typeof byArea === 'object') {
        const cni = Array.isArray(byArea.communicationAndInteraction) ? byArea.communicationAndInteraction : [];
        const cal = Array.isArray(byArea.cognitionAndLearning) ? byArea.cognitionAndLearning : [];
        const semh = Array.isArray(byArea.socialEmotionalMentalHealth) ? byArea.socialEmotionalMentalHealth : [];
        const sp = Array.isArray(byArea.sensoryAndPhysical) ? byArea.sensoryAndPhysical : [];
        setTextCni(cni.join("\n"));
        setTextCal(cal.join("\n"));
        setTextSemh(semh.join("\n"));
        setTextSp(sp.join("\n"));
        const flat = dedupeProvisionLines([...cni, ...cal, ...semh, ...sp]);
        setProvisions(flat.length ? flat : dedupeProvisionLines(list));
        setAutoFilled(false);
      } else if (list.length || (v2 && typeof v2 === 'object')) {
        const buckets = autoClassifyProvisions({ flatList: list, v2Categories: v2 });
        const cni = buckets.communicationAndInteraction;
        const cal = buckets.cognitionAndLearning;
        const semh = buckets.socialEmotionalMentalHealth;
        const sp = buckets.sensoryAndPhysical;
        setTextCni(cni.join("\n"));
        setTextCal(cal.join("\n"));
        setTextSemh(semh.join("\n"));
        setTextSp(sp.join("\n"));
        const merged = dedupeProvisionLines([...cni, ...cal, ...semh, ...sp]);
        setProvisions(merged.length ? merged : dedupeProvisionLines(list));
        setAutoFilled(merged.length > 0);
      } else {
        setProvisions([]);
        setTextCni("");
        setTextCal("");
        setTextSemh("");
        setTextSp("");
        setAutoFilled(false);
      }
    } catch (e) {
      console.warn("Failed to load Section F:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) load();
  }, [studentId]);

  const save = async () => {
    try {
      setSaving(true);
      const cni = parseLines(textCni);
      const cal = parseLines(textCal);
      const semh = parseLines(textSemh);
      const sp = parseLines(textSp);
      const flat = dedupeProvisionLines([...cni, ...cal, ...semh, ...sp]);
      const ref = doc(db, `students/${studentId}`);
      await setDoc(
        ref,
        { ehcpDetails: { sectionF: { provision: flat, provisionByArea: {
          communicationAndInteraction: cni,
          cognitionAndLearning: cal,
          socialEmotionalMentalHealth: semh,
          sensoryAndPhysical: sp,
        } } } },
        { merge: true }
      );
      setProvisions(flat);
      setAutoFilled(false);
      if (typeof onSaved === "function") {
        try { onSaved(); } catch (cbErr) { console.warn("Section F onSaved callback failed:", cbErr); }
      }
    } catch (e) {
      alert("Failed to save Section F provisions. See console.");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
      <h3 className="text-lg font-semibold mb-2">🧩 Section F</h3>
      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <>
          {autoFilled && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              We pre-filled the four areas below from the EHCP extraction using keyword matching. Please review, move any mis-classified lines between areas, then click <span className="font-semibold">Save Provision</span> to commit.
            </div>
          )}
          <p className="text-xs text-gray-600 mb-3">Paste one provision per line under the appropriate area. Include any known frequency/duration.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center"><Icons.ChatBubble className="w-4 h-4 mr-1" /> Communication & Interaction</label>
              <textarea className="w-full border rounded p-2 text-sm h-28 bg-white" value={textCni} onChange={(e) => setTextCni(e.target.value)} placeholder="e.g. 1x/week 30 min SALT small group" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center"><Icons.Brain className="w-4 h-4 mr-1" /> Cognition & Learning</label>
              <textarea className="w-full border rounded p-2 text-sm h-28 bg-white" value={textCal} onChange={(e) => setTextCal(e.target.value)} placeholder="e.g. 3x/week 20 min phonics 1:1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center"><Icons.Heart className="w-4 h-4 mr-1" /> Social, Emotional & Mental Health</label>
              <textarea className="w-full border rounded p-2 text-sm h-28 bg-white" value={textSemh} onChange={(e) => setTextSemh(e.target.value)} placeholder="e.g. Weekly ELSA session 30 min" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center"><Icons.Hand className="w-4 h-4 mr-1" /> Sensory & Physical</label>
              <textarea className="w-full border rounded p-2 text-sm h-28 bg-white" value={textSp} onChange={(e) => setTextSp(e.target.value)} placeholder="e.g. Daily 10 min sensory circuit" />
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <button
              onClick={save}
              disabled={saving}
              className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : <><Icons.Save className="w-4 h-4 mr-1 inline-block" /> Save Provision</>}
            </button>
          </div>

          <div className="mt-4 p-3 bg-white border border-gray-200 rounded">
            <h4 className="font-semibold mb-2 text-gray-800">Current Provision</h4>
            {provisions.length ? (
              <ul className="list-disc ml-5 text-sm space-y-1">
                {provisions.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No provision recorded.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}


